"use client";

import { motion, type PanInfo } from "framer-motion";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudienceAnalytics, useCreatorAnalytics } from "@/lib/analytics";
import { ICONS } from "@artha/icons";
import type { JobItemDto } from "@artha/api-contracts";
import { Button, JobCardPublic, Typography, formatLocation } from "@artha/ui";

import { capitalizeFirst, formatSalary, generateCreatorInviteUrl, generateJobShareUrl, normalizeUsernameParam, shouldHideSalaryBadge } from "@/lib/utils";
import { InstagramLoader } from "@/components/common/InstagramLoader";
import { fetchSimilarJobs } from "@/modules/common/services/jobFeedClient";
import {
  formatExperience,
  sanitizeDescription,
} from "@/components/jobs/jobDetailsUtils";
import { type ApplyClickPortalContext, useApplyClick } from "@/components/jobs/useApplyClick";
import {
  CountryRestrictionModal,
  VpnRestrictionModal,
  useApplyRestrictions,
} from "@/modules/geolocation";
import { useShareJob } from "@/components/share/useShareJob";
import { ShareJobBottomSheet } from "@/components/share/ShareJobBottomSheet";

type JobDetailsDrawerProps = {
  job: JobItemDto | null;
  onClose: () => void;
  onSelectJob?: (job: JobItemDto) => void;
  onApply?: (job: JobItemDto) => void;
  portalContext?: ApplyClickPortalContext;
  creatorName?: string | null;
  creatorAvatarUrl?: string | null;
  /** Override the creator handle used for "View Similar Jobs" navigation.
   *  Required when the drawer is used outside of /@username routes (e.g. /my-link, /dashboard). */
  creatorHandle?: string;
  /** When true, replaces "Apply Now" with "Share Job" that opens the share sheet and the job URL in a new tab.
   *  Use this in creator-facing views (my-link, dashboard). */
  isCreatorView?: boolean;
  /** Authenticated creator id used for share-icon click tracking in creator view. */
  creatorId?: string;
  creatorInviteCode?: string | null;
};

const getWindowHeight = () =>
  typeof window === "undefined"
    ? 0
    : window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight;

const useWindowHeight = () => {
  const [height, setHeight] = useState<number>(getWindowHeight());
  useEffect(() => {
    const resizeListener = () => setHeight(getWindowHeight());
    window.addEventListener("resize", resizeListener);
    return () => window.removeEventListener("resize", resizeListener);
  }, []);
  return height;
};

export const JobDetailsDrawer = ({
  job,
  onClose,
  onSelectJob,
  onApply,
  portalContext,
  creatorName,
  creatorAvatarUrl,
  creatorHandle: creatorHandleProp,
  isCreatorView = false,
  creatorId,
  creatorInviteCode,
}: JobDetailsDrawerProps) => {
  const t = useTranslations("jobDetails");

  const windowHeight = useWindowHeight();
  const pathname = usePathname();
  const router = useRouter();
  const { trackAndRedirect } = useApplyClick(portalContext);
  const { trackJobShareIconClick } = useCreatorAnalytics();
  const [isFullHeight, setIsFullHeight] = useState(false);
  const [currentHeight, setCurrentHeight] = useState<number>(
    Math.max(windowHeight * 0.9, 0)
  );
  const dragStartHeightRef = useRef<number>(Math.max(windowHeight * 0.9, 0));
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [similarJobs, setSimilarJobs] = useState<JobItemDto[]>([]);
  const [similarCount, setSimilarCount] = useState(0);
  const [isSimilarLoading, setIsSimilarLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const { trackAudEarnClick, trackJobView } = useAudienceAnalytics();
  const pathHandle = useMemo(
    () => normalizeUsernameParam(pathname.split("/")[1] ?? ""),
    [pathname]
  );
  const creatorHandle = creatorHandleProp || pathHandle;
  const {
    restriction,
    isCountryModalOpen,
    closeCountryModal,
    isVpnModalOpen,
    closeVpnModal,
    blockIfRestricted,
  } = useApplyRestrictions({
    job,
    enabled: !isCreatorView,
    creatorUsername: creatorHandle,
    creator: { id: creatorId, displayName: creatorName, username: creatorHandle },
  });
  const profilePath = useMemo(
    () => (creatorHandle ? `/@${creatorHandle}` : ""),
    [creatorHandle]
  );

  useEffect(() => {
    if (job?.id) {
      setIsFullHeight(false);
      setCurrentHeight(Math.max(windowHeight * 0.9, 0));

      // Track job view for audience members
      if (!isCreatorView) {
        trackJobView(job);
      }
    }
  }, [job, isCreatorView, trackJobView, windowHeight]);

  useEffect(() => {
    if (!job) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [job]);

  useEffect(() => {
    const loadSimilar = async () => {
      if (!job) return;
      const jobSlug = job.slug;
      if (!jobSlug) {
        setSimilarJobs([]);
        setSimilarCount(0);
        return;
      }
      try {
        setIsSimilarLoading(true);
        const res = await fetchSimilarJobs(jobSlug, 10);
        setSimilarJobs(res.items.slice(0, 5));
        setSimilarCount(res.total);
      } catch {
        setSimilarJobs([]);
        setSimilarCount(0);
      } finally {
        setIsSimilarLoading(false);
      }
    };
    loadSimilar();
  }, [job]);

  const collapsedHeight = useMemo(
    () => Math.max(windowHeight * 0.9, 0),
    [windowHeight]
  );
  const fullHeight = useMemo(
    () => Math.max(windowHeight, collapsedHeight),
    [windowHeight, collapsedHeight]
  );
  const minHeight = useMemo(
    () => Math.max(windowHeight * 0.5, 200),
    [windowHeight]
  );
  const collapsedTop = useMemo(
    () => Math.max(windowHeight || 0, 0),
    [windowHeight]
  );
  const targetTop = useMemo(() => {
    const baseTop = windowHeight - currentHeight;
    const maxTop = Math.max(windowHeight - minHeight, 0);
    return Math.min(Math.max(baseTop, 0), maxTop);
  }, [windowHeight, currentHeight, minHeight]);

  useEffect(() => {
    setCurrentHeight(isFullHeight ? fullHeight : collapsedHeight);
  }, [isFullHeight, fullHeight, collapsedHeight]);

  useEffect(() => {
    setCurrentHeight(isFullHeight ? fullHeight : collapsedHeight);
  }, [windowHeight, collapsedHeight, fullHeight, isFullHeight]);

  const skills = job?.skills?.length ? job.skills : (job?.keywords ?? []);
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const locationLabel = formatLocation({
    city: job?.city,
    state: job?.state,
    country: job?.country,
    location: job?.location,
  });
  const experienceLabel = job ? formatExperience(job) : "";
  const salaryLabel = job ? formatSalary(job) : "";
  const jobShareSlug = job?.slug || job?.id || job?.ref_id || "";
  const shareUrl = useMemo(
    () => generateJobShareUrl(creatorHandle, jobShareSlug),
    [creatorHandle, jobShareSlug]
  );
  const shareMessage = useMemo(
    () =>
      t("shareMessage", {
        jobTitle: job?.title ?? "",
        company: job?.company ?? "",
        location: locationLabel,
        jobUrl: shareUrl,
      }),
    [job?.company, job?.title, locationLabel, shareUrl, t]
  );
  const {
    isOpen: isShareOpen,
    shareUrl: activeShareUrl,
    openShare,
    closeShare,
  } = useShareJob({
    title: job?.title,
    text: shareMessage,
    url: shareUrl,
  });

  useEffect(() => {
    setDescriptionHtml(sanitizeDescription(job?.description));
  }, [job?.description]);

  const clampHeight = (value: number) =>
    Math.min(Math.max(value, minHeight), fullHeight);

  const handleDragStart = () => {
    dragStartHeightRef.current = currentHeight;
  };

  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const next = clampHeight(dragStartHeightRef.current - info.offset.y);
    setCurrentHeight(next);
  };

  const handleHandleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const upThreshold = -60;
    const downThreshold = 80;

    if (info.offset.y < upThreshold) {
      setIsFullHeight(true);
      setCurrentHeight(fullHeight);
      return;
    }

    if (info.offset.y > downThreshold || currentHeight <= windowHeight * 0.4) {
      onClose();
      return;
    }

    setIsFullHeight(false);
    setCurrentHeight(collapsedHeight);
  };

  const normalizedCreatorHandle = useMemo(
    () => normalizeUsernameParam(creatorHandle),
    [creatorHandle]
  );
  const earnHref = useMemo(() => {
    return generateCreatorInviteUrl(creatorInviteCode);
  }, [creatorInviteCode]);

  const handleEarnClick = useCallback(() => {
    if (!normalizedCreatorHandle) return;
    trackAudEarnClick(normalizedCreatorHandle);
  }, [normalizedCreatorHandle, trackAudEarnClick]);

  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const lastScrollTop = useRef(0);
  useEffect(() => {
    if (!job?.id) return;
    setIsFooterVisible(true);
    lastScrollTop.current = 0;
  }, [job?.id]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const isAtEdge = scrollTop <= 0 || scrollTop + clientHeight >= scrollHeight;
    setIsFooterVisible(isAtEdge || scrollTop < lastScrollTop.current);
    lastScrollTop.current = scrollTop;
  };

  if (!job) return null;

  const handleApply = async () => {
    if (isApplying) return;
    if (blockIfRestricted()) return;

    setIsApplying(true);
    try {
      if (job) onApply?.(job);
      await trackAndRedirect(job);
    } finally {
      setIsApplying(false);
    }
  };

  const handleShowSimilarJobs = () => {
    closeCountryModal();
    router.push(profilePath || "/");
    onClose();
  };

  const handleShareJob = () => {
    void trackJobShareIconClick(creatorId);
    void openShare();
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 z-50 flex items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.div
          key={job.id}
          id="creator-job-details-drawer"
          data-experiment-id="creator-job-details-drawer"
          className="creator-job-details-drawer fixed left-1/2 w-full max-w-[437px] -translate-x-1/2 bg-[#FFFAEF] rounded-t-[16px] shadow-[0_-12px_76px_rgba(0,0,0,0.16)] border border-black overflow-hidden"
          initial={{ top: collapsedTop }}
          animate={{
            top: targetTop,
            height: currentHeight,
            maxHeight: currentHeight,
          }}
          exit={{ top: collapsedTop }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 24,
            bounce: 0.35,
          }}
          style={{ height: currentHeight, maxHeight: currentHeight }}
        >
          <div className="flex h-full flex-col">
            <motion.div
              className="flex h-10 items-center justify-center cursor-pointer"
              ref={triggerRef}
              onClick={() => setIsFullHeight((prev) => !prev)}
              title="Drag or click to expand/close"
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onDragEnd={handleHandleDragEnd}
            >
              <span className="h-0.5 w-9 rounded-full bg-[#98A2B3]" />
            </motion.div>

            <div
              className="flex-1 overflow-y-auto overscroll-y-contain px-3 pb-3"
              style={{ scrollbarGutter: "stable" }}
              onScroll={handleScroll}
            >
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-[4px] overflow-hidden bg-white flex items-center justify-center text-sm font-semibold text-[#475467]">
                        {job.logo ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={job.logo}
                            alt={job.company || "Company logo"}
                            className="block h-full w-full object-contain"
                          />
                        ) : (
                          (job.company?.charAt(0) ?? "dY??")
                        )}
                      </div>
                      <Typography
                        variant="small"
                        className="text-[#667085] text-[12px] leading-[normal] whitespace-normal"
                      >
                        {job.company}
                      </Typography>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Typography
                        variant="lead"
                        className="text-[#344054] text-[16px] font-semibold leading-[normal]"
                      >
                        {job.title}
                      </Typography>
                      <div className="flex items-center gap-1 text-[14px] text-[#667085] font-plus-jakarta-sans">
                        <ICONS.LOCATION_PIN className="h-3 w-3 text-[#667085]" />
                        <span className="truncate max-w-[260px]">
                          {locationLabel}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-[14px] text-[#475467]">
                    <MetaBadge
                      icon={
                        <ICONS.OFFICE_BAG className="h-5 w-5 text-[#98A2B3]" />
                      }
                      label={experienceLabel}
                    />
                    <MetaBadge
                      icon={<ICONS.CLOCK className="h-5 w-5 text-[#98A2B3]" />}
                      label={capitalizeFirst(job.job_type)}
                    />
                    {!shouldHideSalaryBadge(job) && (
                      <MetaBadge
                        icon={<ICONS.FRAME className="h-5 w-5 text-[#98A2B3]" />}
                        label={salaryLabel}
                      />
                    )}
                  </div>

                  {skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {skills.map((skill) => (
                        <span
                          key={skill}
                          className="rounded-[4px] border border-[#1877F2] bg-[#E6F9FD] px-2 py-0.5 text-[12px] font-medium text-[#1877F2]"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="border-t border-[#D1D1D6]" />
                  <Typography
                    variant="lead"
                    className="text-[#344054] text-[16px] font-medium "
                  >
                    {t("aboutJobTitle")}
                  </Typography>

                  {descriptionHtml ? (
                    <div
                      className="space-y-3 break-words text-[14px] leading-[20px] text-[#667085] font-plus-jakarta-sans [overflow-wrap:anywhere] [&_p]:text-[#667085] [&_p]:leading-[20px] [&_p]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1"
                      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                  ) : (
                    <Typography
                      variant="lead"
                      className="text-[#667085] text-[14px] font-plus-jakarta-sans"
                    >
                      {t("descriptionFallback")}
                    </Typography>
                  )}
                </div>

                {(isSimilarLoading || similarJobs.length > 0) && (
                  <>
                    <div className="space-y-4">
                      <div className="border-t border-[#D1D1D6]" />
                      <Typography
                        variant="lead"
                        className="text-[#344054] text-[16px] font-medium "
                      >
                        {t("similarJobsTitle")}
                      </Typography>
                      {isSimilarLoading && (
                        <InstagramLoader label={t("loadingSimilarJobs")} />
                      )}

                      {!isSimilarLoading && similarJobs.length > 0 && (
                        <div className="flex gap-3 overflow-x-auto pb-2 items-stretch pr-2">
                          {similarJobs.map((similar, index) => {
                            const similarHref = similar.slug && profilePath
                              ? `${profilePath}/jobs/${encodeURIComponent(similar.slug)}`
                              : similar.url || "#";
                            return (
                              <a
                                key={`${similar.ref_id || similar.id || similar.url || "similar"}`}
                                id={`creator-drawer-similar-job-card-${index}`}
                                data-experiment-id="creator-job-card"
                                href={similarHref}
                                className="creator-job-card-link min-w-[90%] max-w-[90%] shrink-0 text-left flex sm:min-w-[320px] sm:max-w-[360px]"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (onSelectJob) {
                                    onSelectJob(similar);
                                    return;
                                  }
                                  if (similar.url) {
                                    window.open(similar.url, "_blank");
                                  }
                                }}
                              >
                                <JobCardPublic
                                  jobTitle={similar.title}
                                  companyName={similar.company}
                                  companyLogo={similar.logo || ""}
                                  location={similar.location || ""}
                                  city={similar.city}
                                  state={similar.state}
                                  country={similar.country}
                                  salary={formatSalary(similar)}
                                  experience=""
                                  workMode={similar.job_type ?? ""}
                                  skills={
                                    similar.skills?.length
                                      ? similar.skills
                                      : similar.keywords
                                  }
                                  hideSalary={shouldHideSalaryBadge(similar)}
                                  className="h-full"
                                />
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {!isSimilarLoading && similarJobs.length > 0 && job.slug && profilePath ? (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full rounded-[8px] border border-black bg-transparent px-3 py-3 text-[16px] font-medium text-[#667085]"
                      >
                        <Link
                          id="creator-drawer-view-similar-jobs-link"
                          data-experiment-id="creator-view-similar-jobs-link"
                          href={`${profilePath}/search?slug=${encodeURIComponent(job.slug)}`}
                          onClick={onClose}
                        >
                          {t("viewSimilarJobs", { count: similarCount || 0 })}
                        </Link>
                      </Button>
                    ) : !isSimilarLoading && similarJobs.length > 0 ? (
                      <Button
                        variant="outline"
                        className="w-full rounded-[8px] border border-black bg-transparent px-3 py-3 text-[16px] font-medium text-[#667085]"
                        disabled
                      >
                        {t("viewSimilarJobs", { count: similarCount || 0 })}
                      </Button>
                    ) : null}
                  </>
                )}
                {pathname.includes("/@") && (
                  <Button
                    asChild
                    variant="outline"
                    className="creator-earn-cta mt-2 w-full gap-2 rounded-[8px] border border-black bg-white px-3 py-5 text-[16px] font-medium shadow-[0px_4px_0px_0px_#000000]"
                  >
                    <a
                      id="creator-drawer-earn-cta"
                      data-experiment-id="creator-earn-cta"
                      href={earnHref}
                      onClick={handleEarnClick}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ICONS.ARTHA_LINK_BADGE className="h-5 w-5" />
                      {t("earnCta")}
                    </a>
                  </Button>
                )}
                <div className="pb-24" />
              </div>
            </div>

            <div
              id="creator-drawer-floating-actions"
              data-experiment-id="creator-floating-actions"
              className={`creator-floating-actions absolute bottom-0 left-0 right-0 z-10 w-full pb-3 transition-transform duration-500 ease-in-out ${isFooterVisible ? "translate-y-0" : "translate-y-full"
                }`}
            >
              <div className="flex items-center gap-2 px-3 pt-3">
                <Button
                  variant="outline"
                  size="icon"
                  id={isCreatorView ? "creator-drawer-view-job-link" : "creator-drawer-share-job-trigger"}
                  data-experiment-id={isCreatorView ? "creator-view-job-link" : "creator-share-job-trigger"}
                  className="rounded-[8px] border-black bg-white p-[10px] shadow-[0px_4px_0px_0px_#000000] cursor-pointer"
                  aria-label="Share job"
                  onClick={isCreatorView ? () => { if (shareUrl) window.open(shareUrl, "_blank"); } : openShare}
                >
                  {isCreatorView ? (
                    <ICONS.EYEICON className="h-5 w-5 text-[#101828]" />
                  ) : (
                    <ICONS.SHAREICON2 className="h-5 w-5 text-[#101828]" />
                  )}
                </Button>
                {isCreatorView ? (
                  <Button
                    id="creator-drawer-share-job-trigger-primary"
                    data-experiment-id="creator-share-job-trigger"
                    className="flex-1 rounded-[8px] border border-black bg-[#0D41E1] px-[10px] py-[14px] text-[16px] font-medium text-white shadow-[0px_4px_0px_0px_#000000]"
                    onClick={handleShareJob}
                  >
                    Share Job <ICONS.SHAREICON2 className="h-5 w-5" />
                  </Button>
                ) : (
                  <Button
                    id="creator-drawer-apply-job-trigger"
                    data-experiment-id="creator-apply-job-trigger"
                    className="flex-1 rounded-[8px] border border-black bg-[#0D41E1] px-[10px] py-[14px] text-[16px] font-medium text-white shadow-[0px_4px_0px_0px_#000000]"
                    onClick={() => {
                      void handleApply();
                    }}
                    disabled={isApplying}
                  >
                    <span className="inline-flex items-center gap-2">
                      {isApplying ? (
                        <span className="h-4 w-4 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                      ) : null}
                      {isApplying ? "Applying..." : t("applyNow")}
                    </span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      <ShareJobBottomSheet
        isOpen={isShareOpen}
        onClose={closeShare}
        shareUrl={activeShareUrl}
        shareText={shareMessage}
        jobTitle={job.title}
        companyName={job.company}
        companyLogoUrl={job.logo}
        creatorName={creatorName ?? (creatorHandle || undefined)}
        creatorAvatarUrl={creatorAvatarUrl}
        creatorUsername={creatorHandle || undefined}
      />
      <CountryRestrictionModal
        isOpen={isCountryModalOpen}
        onClose={closeCountryModal}
        title={t("countryRestrictionTitle", {
          jobCountry: restriction.jobCountry?.name || job.country || "",
        })}
        subtitle={t("countryRestrictionSub", {
          seekerCountry: restriction.seekerCountry?.name || "",
        })}
        actionLabel={t("countryRestrictionViewSimilarJobs")}
        onShowSimilarJobs={handleShowSimilarJobs}
      />
      <VpnRestrictionModal
        isOpen={isVpnModalOpen}
        onClose={closeVpnModal}
      />
    </>
  );
};

const MetaBadge = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label?: string;
}) => {
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px] font-medium leading-6 text-[#475467]">
      {icon}
      {label}
    </span>
  );
};
